/**
 * WhatsNewPanel - Slide-in side panel showing release notes
 */

import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import PropTypes from "prop-types";
import WhatsNewContent from "./WhatsNewContent";
import { fetchWhatsNewHTML } from "./whatsNewService";
import { useChartsStore } from "../../store/chartStore";
import "./WhatsNew.css";

const WhatsNewPanel = ({ isOpen, version, onClose }) => {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get chart store for activating interactive tools
  const setActiveInteractive = useChartsStore(
    (state) => state.setActiveInteractive,
  );
  const charts = useChartsStore((state) => state.charts);

  // Fetch HTML content when panel opens
  useEffect(() => {
    if (!isOpen || !version) return;

    const loadContent = async () => {
      setLoading(true);
      setError(null);

      try {
        const content = await fetchWhatsNewHTML(version);
        setHtml(content);
      } catch (err) {
        console.error("[WhatsNewPanel] Failed to load content:", err);
        setError("Falha ao carregar novidades. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [isOpen, version]);

  /**
   * Handle CTA actions from HTML content
   */
  const handleAction = ({ action }) => {
    console.log("[WhatsNewPanel] Action triggered:", action);

    switch (action) {
      case "open-import-dialog":
        // TODO: Trigger legacy template import dialog
        console.log("[WhatsNewPanel] Open import dialog action");
        onClose(false);
        break;

      case "open-rating-modal":
        // TODO: Trigger rating modal
        console.log("[WhatsNewPanel] Open rating modal action");
        onClose(false);
        break;

      case "view-changelog":
        // External link - handled by anchor tag
        break;

      case "activate-continuous":
        // Activate the continuous line drawing tool
        console.log("[WhatsNewPanel] Activating continuous tool");
        // Get the first chart's ID to activate the tool
        const chartIds = Object.keys(charts || {});
        if (chartIds.length > 0) {
          // WhatsNew CTA — programmatic arm, not direct engagement.
          setActiveInteractive(chartIds[0], "continuous", {
            skipEngagement: true,
          });
        }
        onClose(false);
        break;

      default:
        console.warn("[WhatsNewPanel] Unknown action:", action);
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    onClose(); // Mark version as seen
  };

  /**
   * Handle backdrop click
   * Note: Backdrop clicks are disabled - user must use close button
   */
  const handleBackdropClick = () => {
    handleClose();
  };

  /**
   * Handle ESC key
   * Note: ESC is disabled - user must use close button
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`whats-new-backdrop ${isOpen ? "open" : ""}`}
        onClick={handleBackdropClick}
        data-evo-id="whats-new-backdrop"
      />

      {/* Side Panel */}
      <div className={`whats-new-panel ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="whats-new-header">
          <div className="whats-new-header-content">
            <div className="whats-new-title">
              <span className="whats-new-icon">✨</span>
              <span>Novidades</span>
            </div>
            <div className="whats-new-version">Versão {version}</div>
          </div>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-rounded whats-new-close-btn"
            onClick={handleClose}
            aria-label="Fechar"
            data-evo-id="whats-new-close-btn"
          />
        </div>

        {/* Body */}
        <div className="whats-new-body">
          {loading && (
            <div className="whats-new-loading">
              <ProgressSpinner
                style={{ width: "50px", height: "50px" }}
                strokeWidth="4"
              />
              <p>Carregando novidades...</p>
            </div>
          )}

          {error && (
            <div className="whats-new-error">
              <i
                className="pi pi-exclamation-triangle"
                style={{ fontSize: "2rem" }}
              ></i>
              <p>{error}</p>
              <Button
                label="Tentar Novamente"
                icon="pi pi-refresh"
                onClick={() => window.location.reload()}
                data-evo-id="whats-new-retry-btn"
              />
            </div>
          )}

          {!loading && !error && html && (
            <WhatsNewContent html={html} onAction={handleAction} />
          )}
        </div>

        {/* Footer */}
        <div className="whats-new-footer">
          <Button
            label="Entendi"
            icon="pi pi-check"
            className="whats-new-ok-btn"
            onClick={handleClose}
            data-evo-id="whats-new-ok-btn"
          />
        </div>
      </div>
    </>
  );
};

WhatsNewPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  version: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default WhatsNewPanel;
